import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import {Loader2, FileDown, Users, TreePine, MapPin, CreditCard, BarChart2, Leaf} from 'lucide-react'
import type { Solicitacao, Configuracao } from '@/types'
import { STATUS_LIST, STATUS_COLORS } from '@/types'
import { formatarData } from '@/utils/protocolo'
import toast from 'react-hot-toast'

const BRAND = '#0f3b50'

type AbaAtiva = 'geral' | 'pessoa' | 'arvore' | 'rua' | 'cpf' | 'replantio'

export default function RelatoriosPage() {
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([])
  const [loading, setLoading] = useState(true)
  const [gerandoPDF, setGerandoPDF] = useState(false)
  const [periodoInicio, setPeriodoInicio] = useState('')
  const [periodoFim, setPeriodoFim] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [abaAtiva, setAbaAtiva] = useState<AbaAtiva>('geral')
  const [busca, setBusca] = useState('')
  const [gerandoPDFReplantio, setGerandoPDFReplantio] = useState(false)

  useEffect(() => {
    lumi.entities.solicitacoes.list({ sort: { createdAt: -1 }, limit: 2000 }).then((r) => {
      setSolicitacoes((r.list || []) as Solicitacao[])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const filtradas = solicitacoes.filter(s => {
    const matchStatus = !filtroStatus || s.status === filtroStatus
    const matchInicio = !periodoInicio || s.createdAt >= periodoInicio
    const matchFim = !periodoFim || s.createdAt <= periodoFim + 'T23:59:59'
    return matchStatus && matchInicio && matchFim
  })

  // ── Agrupamentos ──────────────────────────────────────────────────────
  const porStatus = STATUS_LIST.reduce((acc, s) => {
    acc[s] = filtradas.filter(x => x.status === s).length
    return acc
  }, {} as Record<string, number>)

  // Por bairro
  const porBairro = filtradas.reduce((acc, s) => {
    const k = s.bairroNome || '(sem bairro)'
    acc[k] = (acc[k] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  const bairrosOrdenados = Object.entries(porBairro).sort((a, b) => b[1] - a[1]).slice(0, 10)

  // Por mês
  const porMes = filtradas.reduce((acc, s) => {
    const mes = s.createdAt.slice(0, 7)
    acc[mes] = (acc[mes] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  const mesesOrdenados = Object.entries(porMes).sort((a, b) => a[0].localeCompare(b[0]))
  const maxMes = Math.max(...mesesOrdenados.map(m => m[1]), 1)

  // Por pessoa (nome)
  const porPessoa = filtradas.reduce((acc, s) => {
    const k = s.nomeCompleto || '(sem nome)'
    if (!acc[k]) acc[k] = { total: 0, cpf: s.cpf, ultStatus: s.status }
    acc[k].total += 1
    return acc
  }, {} as Record<string, { total: number; cpf: string; ultStatus: string }>)
  const pessoasOrdenadas = Object.entries(porPessoa).sort((a, b) => b[1].total - a[1].total)

  // Por tipo de árvore
  const porArvore = filtradas.reduce((acc, s) => {
    const k = s.tipoArvore || '(não informado)'
    acc[k] = (acc[k] || 0) + 1
    return acc
  }, {} as Record<string, number>)
  const arvoresOrdenadas = Object.entries(porArvore).sort((a, b) => b[1] - a[1])

  // Por rua
  const porRua = filtradas.reduce((acc, s) => {
    const k = s.ruaNome || '(não informado)'
    if (!acc[k]) acc[k] = { total: 0, bairro: s.bairroNome }
    acc[k].total += 1
    return acc
  }, {} as Record<string, { total: number; bairro: string }>)
  const ruasOrdenadas = Object.entries(porRua).sort((a, b) => b[1].total - a[1].total)

  // Replantio
  const comReplantio     = filtradas.filter(s => s.arvoreReplantada || (s as any).tipoArvoreReplantada)
  const semReplantio     = filtradas.filter(s => s.status === 'Aguardando Replantio')
  const dentroPrazo      = comReplantio.filter(s => s.dentroDoPrezo === true)
  const foraPrazo        = comReplantio.filter(s => s.dentroDoPrezo === false)
  const totalArvoresRem  = filtradas.reduce((acc, s) => acc + (Number(s.quantidadeArvores) || 1), 0)
  const totalReplantadas = comReplantio.reduce((acc, s) => acc + (Number(s.quantidadeArvores) || 1), 0)

  // Por CPF
  const porCPF = filtradas.reduce((acc, s) => {
    const k = s.cpf || '(não informado)'
    if (!acc[k]) acc[k] = { total: 0, nome: s.nomeCompleto }
    acc[k].total += 1
    return acc
  }, {} as Record<string, { total: number; nome: string }>)
  const cpfsOrdenados = Object.entries(porCPF).sort((a, b) => b[1].total - a[1].total)

  // ── Busca nas abas ────────────────────────────────────────────────────
  const buscaL = busca.toLowerCase()
  const pessoasFiltradas = pessoasOrdenadas.filter(([nome, d]) =>
    !buscaL || nome.toLowerCase().includes(buscaL) || d.cpf.includes(buscaL))
  const arvoresFiltradas = arvoresOrdenadas.filter(([tipo]) =>
    !buscaL || tipo.toLowerCase().includes(buscaL))
  const ruasFiltradas = ruasOrdenadas.filter(([rua, d]) =>
    !buscaL || rua.toLowerCase().includes(buscaL) || d.bairro.toLowerCase().includes(buscaL))
  const cpfsFiltrados = cpfsOrdenados.filter(([cpf, d]) =>
    !buscaL || cpf.includes(buscaL) || d.nome.toLowerCase().includes(buscaL))

  // ── Formatação de mês ─────────────────────────────────────────────────
  const formatarMes = (ym: string) => {
    const [y, m] = ym.split('-')
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${meses[parseInt(m) - 1]}/${y}`
  }

  // ── Gerar PDF do período ─────────────────────────────────────────────
  const handleGerarPDFPeriodo = async () => {
    if (filtradas.length === 0) { toast.error('Nenhum registro no período selecionado.'); return }
    setGerandoPDF(true)
    try {
      let cfg: Configuracao | null = null
      try { const r = await lumi.entities.configuracoes.list({ limit: 1 }); cfg = (r.list?.[0] as Configuracao) || null } catch { /* sem config */ }

      const { default: JsPDF } = await import('jspdf')
      const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as any

      const M = 14, W = 210
      const BRAND_C = '#0f3b50'
      const AZUL_CLARO = '#e8f1f5'
      const AZUL_BORDA = '#b8d4e0'
      const AZUL_MEDIO = '#1a5470'
      const CINZA_500 = '#6b7280'
      const CINZA_700 = '#374151'
      const GEOA_LOGO = 'https://file.lumi.new/p455908949713027072/SPF2065453480390553600.jpg'

      const fromHex = (h: string): [number,number,number] => [parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)]

      // Pré-carrega logo GEOA via canvas
      let geoaDataURL: string | undefined
      try {
        geoaDataURL = await new Promise<string>((resolve, reject) => {
          const img = new Image(); img.crossOrigin = 'anonymous'
          img.onload = () => {
            const c = document.createElement('canvas')
            c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height
            const ctx = c.getContext('2d')!; ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0)
            resolve(c.toDataURL('image/png'))
          }
          img.onerror = () => reject(); img.src = GEOA_LOGO
        })
      } catch { geoaDataURL = undefined }

      // Função de cabeçalho institucional
      const drawRelatorioHeader = (pageNum: number) => {
        const HDR_H = 33; const SUB_H = 7.5
        // Fundo branco
        doc.setFillColor(255, 255, 255); doc.rect(0, 0, W, HDR_H + SUB_H + 1.2, 'F')
        // Logo Prefeitura
        const logoSize = 22; const logoX = M; const logoY = (HDR_H - logoSize) / 2
        if (cfg?.logoUrl) {
          try { doc.addImage(cfg.logoUrl, 'PNG', logoX, logoY, logoSize, logoSize) }
          catch { try { doc.addImage(cfg.logoUrl, 'JPEG', logoX, logoY, logoSize, logoSize) } catch {} }
        } else {
          doc.setFillColor(...fromHex(AZUL_CLARO)); doc.setDrawColor(...fromHex(AZUL_BORDA)); doc.setLineWidth(0.1)
          doc.rect(logoX, logoY, logoSize, logoSize, 'FD')
          doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...fromHex(BRAND_C))
          doc.text('PM', logoX + logoSize / 2, logoY + logoSize / 2 + 1.5, { align: 'center' })
        }
        // Logo GEOA (direita)
        const gW = 37; const gH = 15; const gX = W - M - gW; const gY = (HDR_H - gH) / 2
        doc.setFillColor(255, 255, 255); doc.setDrawColor(...fromHex(AZUL_BORDA)); doc.setLineWidth(0.4)
        doc.rect(gX - 2, gY - 2, gW + 4, gH + 4, 'FD')
        if (geoaDataURL) { try { doc.addImage(geoaDataURL, 'PNG', gX, gY, gW, gH) } catch {} }
        else {
          doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...fromHex(BRAND_C))
          doc.text('GEOA', gX + gW / 2, gY + gH / 2 + 1, { align: 'center' })
        }
        // Textos institucionais
        const tX = logoX + logoSize + 5; const tMaxW = gX - 4 - tX
        const nomePref = (cfg?.nomePrefeitura || 'PREFEITURA MUNICIPAL DE SÃO JOSÉ DO SERIDÓ').toUpperCase()
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...fromHex(BRAND_C))
        doc.text(nomePref, tX, 9, { maxWidth: tMaxW })
        const sec = cfg?.bairro ? `Secretaria Municipal – ${cfg.bairro}` : 'Secretaria Municipal de Meio Ambiente'
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...fromHex(CINZA_700))
        doc.text(sec, tX, 15)
        const endParts: string[] = []
        if (cfg?.endereco) endParts.push(cfg.endereco)
        if (cfg?.cep) endParts.push(`CEP: ${cfg.cep}`)
        if (endParts.length) { doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_500)); doc.text(endParts.join('   |   '), tX, 20.5, { maxWidth: tMaxW }) }
        const contatos: string[] = []
        if (cfg?.cnpj) contatos.push(`CNPJ: ${cfg.cnpj}`)
        if (cfg?.telefone) contatos.push(`Tel: ${cfg.telefone}`)
        if (cfg?.email) contatos.push(cfg.email)
        if (contatos.length) { doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_500)); doc.text(contatos.join('   |   '), tX, 26, { maxWidth: tMaxW }) }
        // Linha azul
        doc.setFillColor(...fromHex(BRAND_C)); doc.rect(0, HDR_H, W, 1.2, 'F')
        // Faixa subtitulo
        doc.setFillColor(...fromHex(AZUL_CLARO)); doc.rect(0, HDR_H + 1.2, W, SUB_H, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.8); doc.setTextColor(...fromHex(BRAND_C))
        doc.text('GEOA – Relatório de Solicitações  –  Sistema de Remoção de Árvores  –  São José do Seridó/RN', W / 2, HDR_H + 1.2 + SUB_H - 2, { align: 'center' })
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8); doc.setTextColor(...fromHex(CINZA_500))
        doc.text(`Relatório  •  Pág. ${pageNum}`, W - M, HDR_H + 1.2 + SUB_H - 2, { align: 'right' })
      }

      drawRelatorioHeader(1)
      let y = 46

      // ── Faixa de período + filtros ────────────────────────────────────────
      doc.setFillColor(...fromHex(AZUL_CLARO))
      doc.rect(M, y, W - M * 2, 16, 'F')
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, 2, 16, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...fromHex(BRAND_C))
      const periodo = periodoInicio || periodoFim
        ? `Período: ${periodoInicio ? formatarData(periodoInicio) : 'início'} a ${periodoFim ? formatarData(periodoFim) : 'hoje'}`
        : 'Período: Todos os registros'
      doc.text(periodo, M + 6, y + 6)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_700))
      doc.text(`${filtradas.length} registro(s)  |  Status: ${filtroStatus || 'Todos'}  |  Emitido em: ${new Date().toLocaleString('pt-BR')}`, M + 6, y + 12)
      y += 22

      // ── KPIs ─────────────────────────────────────────────────────────────
      const kpis = [
        { label: 'Total',      valor: filtradas.length,                                                  cor: BRAND_C },
        { label: 'Aprovadas',  valor: filtradas.filter(s => s.status === 'Aprovada').length,             cor: '#166534' },
        { label: 'Reprovadas', valor: filtradas.filter(s => s.status === 'Reprovada').length,            cor: '#991b1b' },
        { label: 'Concluídas', valor: filtradas.filter(s => s.status === 'Processo Concluído').length,  cor: '#065f46' },
      ]
      const kW = (W - M * 2 - 9) / 4
      kpis.forEach((k, i) => {
        const kx = M + i * (kW + 3)
        doc.setFillColor(255, 255, 255); doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.2)
        doc.rect(kx, y, kW, 16, 'FD')
        doc.setFillColor(...fromHex(k.cor)); doc.rect(kx, y, kW, 2.5, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...fromHex(k.cor))
        doc.text(String(k.valor), kx + kW / 2, y + 11.5, { align: 'center' })
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(...fromHex(CINZA_500))
        doc.text(k.label, kx + kW / 2, y + 14.8, { align: 'center' })
      })
      y += 22

      // ── Solicitações por Status ────────────────────────────────────────────
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, W - M * 2, 7, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(255, 255, 255)
      doc.text('SOLICITAÇÕES POR STATUS', M + 5, y + 4.8)
      y += 10
      const barW = W - M * 2 - 72
      STATUS_LIST.forEach((st, idx) => {
        const count = porStatus[st] || 0
        const pct   = filtradas.length > 0 ? (count / filtradas.length) * 100 : 0
        if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(M, y - 0.5, W - M * 2, 6, 'F') }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_700))
        doc.text(st, M + 3, y + 3.8)
        doc.setFillColor(229, 231, 235); doc.rect(M + 60, y + 1, barW, 3.2, 'F')
        if (pct > 0) { doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M + 60, y + 1, barW * pct / 100, 3.2, 'F') }
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(BRAND_C))
        doc.text(String(count), W - M - 2, y + 3.8, { align: 'right' })
        y += 6
      })
      y += 5

      // ── Por Tipo de Árvore ────────────────────────────────────────────────
      if (arvoresOrdenadas.length > 0) {
        if (y > 240) { doc.addPage(); drawRelatorioHeader(doc.getNumberOfPages()); y = 46 }
        doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, W - M * 2, 7, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(255, 255, 255)
        doc.text('TIPOS DE ÁRVORE MAIS SOLICITADOS', M + 5, y + 4.8)
        y += 10
        const arvW = W - M * 2 - 72
        arvoresOrdenadas.slice(0, 8).forEach(([tipo, count], idx) => {
          if (idx % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(M, y - 0.5, W - M * 2, 6, 'F') }
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_700))
          doc.text(tipo.slice(0, 30), M + 3, y + 3.8)
          const pct = filtradas.length > 0 ? (count / filtradas.length) * 100 : 0
          doc.setFillColor(229, 231, 235); doc.rect(M + 60, y + 1, arvW, 3.2, 'F')
          if (pct > 0) { doc.setFillColor(...fromHex(AZUL_MEDIO)); doc.rect(M + 60, y + 1, arvW * pct / 100, 3.2, 'F') }
          doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(AZUL_MEDIO))
          doc.text(String(count), W - M - 2, y + 3.8, { align: 'right' })
          y += 6
        })
        y += 5
      }

      // ── Listagem detalhada ────────────────────────────────────────────────
      if (y > 210) { doc.addPage(); drawRelatorioHeader(doc.getNumberOfPages()); y = 46 }
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, W - M * 2, 7, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(255, 255, 255)
      doc.text(`LISTAGEM DE SOLICITAÇÕES (${filtradas.length} registro(s))`, M + 5, y + 4.8)
      y += 10

      // Cabeçalho da tabela — colunas calibradas para A4 (196mm úteis)
      // Protocolo: 33mm | Solicitante: 58mm | Data: 20mm | Árvore: 30mm | Status: restante
      const tableW = W - M * 2  // 182mm
      const C = {
        prot:   M + 2,
        nome:   M + 35,
        data:   M + 95,
        arvore: M + 118,
        status: M + 152,
      }
      // Linha superior do cabeçalho
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, tableW, 0.8, 'F')
      doc.setFillColor(...fromHex(AZUL_CLARO)); doc.rect(M, y + 0.8, tableW, 6, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(...fromHex(BRAND_C))
      doc.text('PROTOCOLO',   C.prot,   y + 5)
      doc.text('SOLICITANTE', C.nome,   y + 5)
      doc.text('DATA',        C.data,   y + 5)
      doc.text('ÁRVORE',      C.arvore, y + 5)
      doc.text('STATUS',      C.status, y + 5)
      // Linha inferior do cabeçalho
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y + 6.8, tableW, 0.5, 'F')
      y += 9

      filtradas.forEach((s, i) => {
        if (y > 277) { doc.addPage(); drawRelatorioHeader(doc.getNumberOfPages()); y = 46 }
        // Zebra suave
        if (i % 2 === 0) {
          doc.setFillColor(248, 251, 253); doc.rect(M, y - 0.5, tableW, 5.8, 'F')
        } else {
          doc.setFillColor(255, 255, 255); doc.rect(M, y - 0.5, tableW, 5.8, 'F')
        }
        // Separador de linha
        doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.08)
        doc.line(M, y + 5.3, M + tableW, y + 5.3)

        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(17, 24, 39)
        doc.text((s.protocolo || '—').slice(0, 16),              C.prot,   y + 3.5)
        doc.text((s.nomeCompleto || '—').slice(0, 28),           C.nome,   y + 3.5)
        const dataStr = s.createdAt ? new Date(s.createdAt).toLocaleDateString('pt-BR') : '—'
        doc.setTextColor(...fromHex(CINZA_500))
        doc.text(dataStr,                                         C.data,   y + 3.5)
        doc.setTextColor(17, 24, 39)
        doc.text((s.tipoArvore || '—').slice(0, 16),             C.arvore, y + 3.5)
        doc.setTextColor(...fromHex(CINZA_700))
        doc.text((s.status || '—').slice(0, 20),                 C.status, y + 3.5)
        y += 5.8
      })
      // Linha de fecho da tabela
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, tableW, 0.5, 'F')

      // Rodapé em todas as páginas
      const totalPgs = doc.getNumberOfPages()
      for (let p = 1; p <= totalPgs; p++) {
        doc.setPage(p)
        doc.setFillColor(...fromHex(BRAND_C)); doc.rect(0, 287, W, 10, 'F')
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(255, 255, 255)
        doc.text(`GEOA – Gestão Online Ambiental  |  ${cfg?.nomePrefeitura || 'Prefeitura Municipal de São José do Seridó/RN'}`, M, 293)
        doc.text(`Página ${p} de ${totalPgs}`, W - M, 293, { align: 'right' })
      }

      const nomeArq = `Relatorio_${periodoInicio || 'inicio'}_${periodoFim || 'fim'}.pdf`
      doc.save(nomeArq)
      toast.success('PDF do relatório gerado!')
    } catch (err) {
      console.error(err)
      toast.error('Erro ao gerar PDF.')
    } finally {
      setGerandoPDF(false)
    }
  }

  const inputCls = 'w-full px-3 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white'

  // ── PDF consolidado de replantio ───────────────────────────────────────
  const handleGerarPDFReplantio = async () => {
    if (filtradas.length === 0) { toast.error('Nenhum registro no período selecionado.'); return }
    setGerandoPDFReplantio(true)
    try {
      let cfg: Configuracao | null = null
      try { const r = await lumi.entities.configuracoes.list({ limit: 1 }); cfg = (r.list?.[0] as Configuracao) || null } catch { /* sem config */ }

      const { default: JsPDF } = await import('jspdf')
      const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' }) as any

      const M = 14, W = 210
      const BRAND_C = '#0f3b50'
      const VERDE    = '#166534'
      const LARANJA  = '#92400e'
      const VERMELHO = '#991b1b'
      const AZUL_CLARO = '#e8f1f5'
      const AZUL_BORDA = '#b8d4e0'
      const AZUL_MEDIO = '#1a5470'
      const CINZA_500 = '#6b7280'
      const CINZA_700 = '#374151'
      const GEOA_LOGO = 'https://file.lumi.new/p455908949713027072/SPF2065453480390553600.jpg'

      const fromHex = (h: string): [number,number,number] => [parseInt(h.slice(1,3),16),parseInt(h.slice(3,5),16),parseInt(h.slice(5,7),16)]

      let geoaDataURL: string | undefined
      try {
        geoaDataURL = await new Promise<string>((resolve, reject) => {
          const img = new Image(); img.crossOrigin = 'anonymous'
          img.onload = () => {
            const c = document.createElement('canvas')
            c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height
            const ctx = c.getContext('2d')!; ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0)
            resolve(c.toDataURL('image/png'))
          }
          img.onerror = () => reject(); img.src = GEOA_LOGO
        })
      } catch { geoaDataURL = undefined }

      const drawHdr = (pgNum: number) => {
        const HDR_H = 33; const SUB_H = 7.5
        doc.setFillColor(255, 255, 255); doc.rect(0, 0, W, HDR_H + SUB_H + 1.2, 'F')
        const lSz = 22; const lX = M; const lY = (HDR_H - lSz) / 2
        if (cfg?.logoUrl) {
          try { doc.addImage(cfg.logoUrl, 'PNG', lX, lY, lSz, lSz) }
          catch { try { doc.addImage(cfg.logoUrl, 'JPEG', lX, lY, lSz, lSz) } catch {} }
        } else {
          doc.setFillColor(...fromHex(AZUL_CLARO)); doc.setDrawColor(...fromHex(AZUL_BORDA)); doc.setLineWidth(0.1)
          doc.rect(lX, lY, lSz, lSz, 'FD'); doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...fromHex(BRAND_C))
          doc.text('PM', lX + lSz / 2, lY + lSz / 2 + 1.5, { align: 'center' })
        }
        const gW = 37; const gH = 15; const gX = W - M - gW; const gY = (HDR_H - gH) / 2
        doc.setFillColor(255, 255, 255); doc.setDrawColor(...fromHex(AZUL_BORDA)); doc.setLineWidth(0.4)
        doc.rect(gX - 2, gY - 2, gW + 4, gH + 4, 'FD')
        if (geoaDataURL) { try { doc.addImage(geoaDataURL, 'PNG', gX, gY, gW, gH) } catch {} }
        else { doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...fromHex(BRAND_C)); doc.text('GEOA', gX + gW / 2, gY + gH / 2 + 1, { align: 'center' }) }
        const tX = lX + lSz + 5; const tMaxW = gX - 4 - tX
        const nomePref = (cfg?.nomePrefeitura || 'PREFEITURA MUNICIPAL DE SÃO JOSÉ DO SERIDÓ').toUpperCase()
        doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...fromHex(BRAND_C))
        doc.text(nomePref, tX, 9, { maxWidth: tMaxW })
        const sec = cfg?.bairro ? `Secretaria Municipal – ${cfg.bairro}` : 'Secretaria Municipal de Meio Ambiente'
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...fromHex(CINZA_700)); doc.text(sec, tX, 15)
        const endParts: string[] = []; if (cfg?.endereco) endParts.push(cfg.endereco); if (cfg?.cep) endParts.push(`CEP: ${cfg.cep}`)
        if (endParts.length) { doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_500)); doc.text(endParts.join('   |   '), tX, 20.5, { maxWidth: tMaxW }) }
        const contatos: string[] = []; if (cfg?.cnpj) contatos.push(`CNPJ: ${cfg.cnpj}`); if (cfg?.telefone) contatos.push(`Tel: ${cfg.telefone}`); if (cfg?.email) contatos.push(cfg.email)
        if (contatos.length) { doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_500)); doc.text(contatos.join('   |   '), tX, 26, { maxWidth: tMaxW }) }
        doc.setFillColor(...fromHex(BRAND_C)); doc.rect(0, HDR_H, W, 1.2, 'F')
        doc.setFillColor(...fromHex(AZUL_CLARO)); doc.rect(0, HDR_H + 1.2, W, SUB_H, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(6.8); doc.setTextColor(...fromHex(BRAND_C))
        doc.text('GEOA – Relatório de Replantio  –  Sistema de Remoção de Árvores  –  São José do Seridó/RN', W / 2, HDR_H + 1.2 + SUB_H - 2, { align: 'center' })
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8); doc.setTextColor(...fromHex(CINZA_500))
        doc.text(`Replantio  •  Pág. ${pgNum}`, W - M, HDR_H + 1.2 + SUB_H - 2, { align: 'right' })
      }

      drawHdr(1)
      let y = 46

      // ── Cabeçalho informativo ──────────────────────────────────────────────
      doc.setFillColor(...fromHex(AZUL_CLARO)); doc.rect(M, y, W - M * 2, 16, 'F')
      doc.setFillColor(...fromHex(BRAND_C)); doc.rect(M, y, 2, 16, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5); doc.setTextColor(...fromHex(BRAND_C))
      const periodo = periodoInicio || periodoFim
        ? `Período: ${periodoInicio ? formatarData(periodoInicio) : 'início'} a ${periodoFim ? formatarData(periodoFim) : 'hoje'}`
        : 'Período: Todos os registros'
      doc.text(periodo, M + 6, y + 6)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_700))
      doc.text(`${filtradas.length} solicitações filtradas  |  Emitido em: ${new Date().toLocaleString('pt-BR')}`, M + 6, y + 12)
      y += 22

      // ── KPIs de replantio ──────────────────────────────────────────────────
      const totalRemovidas = filtradas.reduce((acc, s) => acc + (Number(s.quantidadeArvores) || 1), 0)
      const totalReplantadasPDF = comReplantio.reduce((acc, s) => acc + (Number(s.quantidadeArvores) || 1), 0)
      const kpiItems = [
        { label: 'Solicitações',      valor: filtradas.length,        cor: BRAND_C },
        { label: 'Árv. Removidas',    valor: totalRemovidas,          cor: AZUL_MEDIO },
        { label: 'Árv. Replantadas',   valor: totalReplantadasPDF,    cor: VERDE },
        { label: 'Aguard. Replantio', valor: semReplantio.length,     cor: LARANJA },
      ]
      const kW = (W - M * 2 - 9) / 4
      kpiItems.forEach((k, i) => {
        const kx = M + i * (kW + 3)
        doc.setFillColor(255, 255, 255); doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.2)
        doc.rect(kx, y, kW, 18, 'FD')
        doc.setFillColor(...fromHex(k.cor)); doc.rect(kx, y, kW, 2.5, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(...fromHex(k.cor))
        doc.text(String(k.valor), kx + kW / 2, y + 13, { align: 'center' })
        doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8); doc.setTextColor(...fromHex(CINZA_500))
        doc.text(k.label, kx + kW / 2, y + 16.5, { align: 'center' })
      })
      y += 24

      // ── Taxa de cumprimento e prazo ────────────────────────────────────────
      const taxaCumprimento = filtradas.length > 0 ? Math.round((comReplantio.length / filtradas.length) * 100) : 0
      const taxaPrazo       = comReplantio.length > 0 ? Math.round((dentroPrazo.length / comReplantio.length) * 100) : 0

      doc.setFillColor(255, 255, 255); doc.setDrawColor(...fromHex(AZUL_BORDA)); doc.setLineWidth(0.2)
      doc.rect(M, y, W - M * 2, 22, 'FD')
      doc.setFillColor(...fromHex(VERDE)); doc.rect(M, y, W - M * 2, 2.5, 'F')
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...fromHex(BRAND_C))
      doc.text('INDICADORES DE CUMPRIMENTO DA OBRIGAÇÃO DE REPLANTIO', M + 5, y + 8)
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(...fromHex(CINZA_700))
      doc.text(`Taxa geral de replantio: ${taxaCumprimento}%  (${comReplantio.length} de ${filtradas.length} solicitações com replantio concluído)`, M + 5, y + 14)
      doc.text(`Replantios dentro do prazo de 15 dias: ${dentroPrazo.length}  |  Fora do prazo: ${foraPrazo.length}  |  Prazo não informado: ${comReplantio.length - dentroPrazo.length - foraPrazo.length}  |  Taxa de cumprimento no prazo: ${taxaPrazo}%`, M + 5, y + 19)
      y += 28

      // ── Tabela detalhada de replantios realizados ─────────────────────────
      if (comReplantio.length > 0) {
        if (y > 210) { doc.addPage(); drawHdr(doc.getNumberOfPages()); y = 46 }
        doc.setFillColor(...fromHex(VERDE)); doc.rect(M, y, W - M * 2, 7, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(255, 255, 255)
        doc.text(`REPLANTIOS REALIZADOS (${comReplantio.length} registro(s))`, M + 5, y + 4.8)
        y += 10

        const tW = W - M * 2
        doc.setFillColor(...fromHex(VERDE)); doc.rect(M, y, tW, 0.8, 'F')
        doc.setFillColor(232, 248, 238); doc.rect(M, y + 0.8, tW, 6, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(5.8); doc.setTextColor(...fromHex(VERDE))
        doc.text('PROTOCOLO',    M + 2,   y + 5)
        doc.text('SOLICITANTE',  M + 36,  y + 5)
        doc.text('DATA REM.',    M + 90,  y + 5)
        doc.text('TIPO REPLANT.',M + 112, y + 5)
        doc.text('LOCAL',        M + 148, y + 5)
        doc.text('PRAZO',        M + 173, y + 5)
        doc.setFillColor(...fromHex(VERDE)); doc.rect(M, y + 6.8, tW, 0.5, 'F')
        y += 9

        comReplantio.forEach((s, i) => {
          if (y > 277) { doc.addPage(); drawHdr(doc.getNumberOfPages()); y = 46 }
          const zebra = i % 2 === 0 ? [248, 255, 251] : [255, 255, 255]
          doc.setFillColor(zebra[0], zebra[1], zebra[2]); doc.rect(M, y - 0.5, tW, 5.8, 'F')
          doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.08); doc.line(M, y + 5.3, M + tW, y + 5.3)
          const prazoTexto = s.dentroDoPrezo === true ? 'Sim' : s.dentroDoPrezo === false ? 'Não' : '—'
          const prazoColor = s.dentroDoPrezo === true ? fromHex(VERDE) : s.dentroDoPrezo === false ? fromHex(VERMELHO) : fromHex(CINZA_500)
          const tipoRepl = (s as any).tipoArvoreReplantada || s.arvoreReplantada || '—'
          const localRepl = (s as any).enderecoReplantio || s.ondeReplantio || (s.localReplantio === 'mesmo' ? 'Mesmo local' : '—')
          doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8); doc.setTextColor(17, 24, 39)
          doc.text((s.protocolo || '—').slice(0, 16),          M + 2,   y + 3.5)
          doc.text((s.nomeCompleto || '—').slice(0, 25),       M + 36,  y + 3.5)
          doc.setTextColor(...fromHex(CINZA_500))
          const dataRem = s.createdAt ? new Date(s.createdAt).toLocaleDateString('pt-BR') : '—'
          doc.text(dataRem,                                     M + 90,  y + 3.5)
          doc.setTextColor(17, 24, 39)
          doc.text(tipoRepl.slice(0, 20),                       M + 112, y + 3.5)
          doc.text(localRepl.slice(0, 18),                      M + 148, y + 3.5)
          doc.setTextColor(...prazoColor)
          doc.text(prazoTexto,                                  M + 173, y + 3.5)
          y += 5.8
        })
        doc.setFillColor(...fromHex(VERDE)); doc.rect(M, y, tW, 0.5, 'F')
        y += 10
      }

      // ── Tabela de solicitações aguardando replantio ──────────────────────
      if (semReplantio.length > 0) {
        if (y > 220) { doc.addPage(); drawHdr(doc.getNumberOfPages()); y = 46 }
        doc.setFillColor(...fromHex(LARANJA)); doc.rect(M, y, W - M * 2, 7, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(255, 255, 255)
        doc.text(`AGUARDANDO REPLANTIO (${semReplantio.length} registro(s))`, M + 5, y + 4.8)
        y += 10

        const tW = W - M * 2
        doc.setFillColor(...fromHex(LARANJA)); doc.rect(M, y, tW, 0.8, 'F')
        doc.setFillColor(255, 247, 230); doc.rect(M, y + 0.8, tW, 6, 'F')
        doc.setFont('helvetica', 'bold'); doc.setFontSize(5.8); doc.setTextColor(...fromHex(LARANJA))
        doc.text('PROTOCOLO',   M + 2,  y + 5)
        doc.text('SOLICITANTE', M + 36, y + 5)
        doc.text('CPF',         M + 100, y + 5)
        doc.text('TIPO ÁRVORE', M + 130, y + 5)
        doc.text('DATA ABER.',  M + 165, y + 5)
        doc.setFillColor(...fromHex(LARANJA)); doc.rect(M, y + 6.8, tW, 0.5, 'F')
        y += 9

        semReplantio.forEach((s, i) => {
          if (y > 277) { doc.addPage(); drawHdr(doc.getNumberOfPages()); y = 46 }
          const zebra = i % 2 === 0 ? [255, 253, 245] : [255, 255, 255]
          doc.setFillColor(zebra[0], zebra[1], zebra[2]); doc.rect(M, y - 0.5, tW, 5.8, 'F')
          doc.setDrawColor(229, 231, 235); doc.setLineWidth(0.08); doc.line(M, y + 5.3, M + tW, y + 5.3)
          doc.setFont('helvetica', 'normal'); doc.setFontSize(5.8); doc.setTextColor(17, 24, 39)
          doc.text((s.protocolo || '—').slice(0, 16),   M + 2,   y + 3.5)
          doc.text((s.nomeCompleto || '—').slice(0, 28), M + 36,  y + 3.5)
          doc.setTextColor(...fromHex(CINZA_500))
          doc.text((s.cpf || '—').slice(0, 18),          M + 100, y + 3.5)
          doc.setTextColor(17, 24, 39)
          doc.text((s.tipoArvore || '—').slice(0, 18),   M + 130, y + 3.5)
          const dataAb = s.createdAt ? new Date(s.createdAt).toLocaleDateString('pt-BR') : '—'
          doc.setTextColor(...fromHex(CINZA_500))
          doc.text(dataAb,                               M + 165, y + 3.5)
          y += 5.8
        })
        doc.setFillColor(...fromHex(LARANJA)); doc.rect(M, y, tW, 0.5, 'F')
      }

      // ── Rodapé ──────────────────────────────────────────────────────────────
      const totalPgs = doc.getNumberOfPages()
      for (let p = 1; p <= totalPgs; p++) {
        doc.setPage(p)
        doc.setFillColor(...fromHex(BRAND_C)); doc.rect(0, 287, W, 10, 'F')
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(255, 255, 255)
        doc.text(`GEOA – Gestão Online Ambiental  |  ${cfg?.nomePrefeitura || 'Prefeitura Municipal de São José do Seridó/RN'}`, M, 293)
        doc.text(`Página ${p} de ${totalPgs}`, W - M, 293, { align: 'right' })
      }

      const nomeArq = `Relatorio_Replantio_${periodoInicio || 'inicio'}_${periodoFim || 'fim'}.pdf`
      doc.save(nomeArq)
      toast.success('PDF de replantio gerado!')
    } catch (err) {
      console.error(err)
      toast.error('Erro ao gerar PDF de replantio.')
    } finally {
      setGerandoPDFReplantio(false)
    }
  }

  const abas: { id: AbaAtiva; label: string; icon: any; count: number }[] = [
    { id: 'geral',     label: 'Geral',          icon: BarChart2,  count: filtradas.length },
    { id: 'pessoa',    label: 'Por Pessoa',      icon: Users,      count: pessoasOrdenadas.length },
    { id: 'arvore',    label: 'Por Árvore',      icon: TreePine,   count: arvoresOrdenadas.length },
    { id: 'rua',       label: 'Por Rua',         icon: MapPin,     count: ruasOrdenadas.length },
    { id: 'cpf',       label: 'Por CPF',         icon: CreditCard, count: cpfsOrdenados.length },
    { id: 'replantio', label: 'Replantio',       icon: Leaf,       count: comReplantio.length },
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-sm text-gray-500 mt-0.5">Histórico e análise das solicitações</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGerarPDFReplantio}
            disabled={gerandoPDFReplantio || loading}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-2xl transition disabled:opacity-50"
            style={{ backgroundColor: '#166534' }}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#14532d'}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#166534'}
            title="Baixar PDF de Replantio"
          >
            {gerandoPDFReplantio ? <Loader2 className="w-4 h-4 animate-spin" /> : <Leaf className="w-4 h-4" />}
            <span className="hidden sm:inline">{gerandoPDFReplantio ? 'Gerando...' : 'PDF Replantio'}</span>
          </button>
          <button
            onClick={handleGerarPDFPeriodo}
            disabled={gerandoPDF || loading}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-2xl transition disabled:opacity-50"
            style={{ backgroundColor: BRAND }}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#0a2d3d'}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND}
          >
            {gerandoPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            <span className="hidden sm:inline">{gerandoPDF ? 'Gerando...' : 'Baixar PDF'}</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Período — Início</label>
            <input type="date" value={periodoInicio} onChange={e => setPeriodoInicio(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Período — Fim</label>
            <input type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} className={inputCls}>
              <option value="">Todos</option>
              {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: BRAND }} />
          <span className="text-sm text-gray-500">Carregando...</span>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total',      value: filtradas.length,                                                 color: 'text-gray-900' },
              { label: 'Aprovadas',  value: filtradas.filter(s => s.status === 'Aprovada').length,           color: 'text-[#0f3b50]' },
              { label: 'Reprovadas', value: filtradas.filter(s => s.status === 'Reprovada').length,          color: 'text-red-600' },
              { label: 'Concluídas', value: filtradas.filter(s => s.status === 'Processo Concluído').length, color: 'text-blue-700' },
            ].map(c => (
              <div key={c.label} className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm">
                <p className="text-xs text-gray-500 mb-1">{c.label}</p>
                <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
              </div>
            ))}
          </div>

          {/* Abas */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Tab bar */}
            <div className="flex overflow-x-auto border-b border-gray-100">
              {abas.map(a => {
                const Icon = a.icon
                const active = abaAtiva === a.id
                return (
                  <button key={a.id} onClick={() => { setAbaAtiva(a.id); setBusca('') }}
                    className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition flex-shrink-0
                      ${active ? 'border-[#0f3b50] text-[#0f3b50] bg-[#f0f7fa]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {a.label}
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${active ? 'bg-[#0f3b50] text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {a.count}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="p-5">

              {/* ── ABA GERAL ── */}
              {abaAtiva === 'geral' && (
                <div className="space-y-6">
                  {/* Por Status */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Por Status</h3>
                    <div className="space-y-2.5">
                      {STATUS_LIST.map(s => {
                        const count = porStatus[s] || 0
                        const pct = filtradas.length > 0 ? (count / filtradas.length) * 100 : 0
                        return (
                          <div key={s} className="flex items-center gap-3">
                            <div className="w-40 text-xs text-gray-600 flex-shrink-0 truncate">{s}</div>
                            <div className="flex-1 bg-gray-100 rounded-full h-2">
                              <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: BRAND }} />
                            </div>
                            <span className="text-xs font-medium text-gray-700 w-8 text-right">{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Por Bairro */}
                  {bairrosOrdenados.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Bairros</h3>
                      <div className="space-y-2.5">
                        {bairrosOrdenados.map(([bairro, count]) => {
                          const pct = filtradas.length > 0 ? (count / filtradas.length) * 100 : 0
                          return (
                            <div key={bairro} className="flex items-center gap-3">
                              <div className="w-40 text-xs text-gray-600 flex-shrink-0 truncate">{bairro}</div>
                              <div className="flex-1 bg-gray-100 rounded-full h-2">
                                <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#3b82f6' }} />
                              </div>
                              <span className="text-xs font-medium text-gray-700 w-8 text-right">{count}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Por Mês */}
                  {mesesOrdenados.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Por Mês</h3>
                      <div className="flex items-end gap-2 h-32 overflow-x-auto">
                        {mesesOrdenados.map(([mes, count]) => (
                          <div key={mes} className="flex flex-col items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-gray-700 font-medium">{count}</span>
                            <div className="w-8 rounded-t transition-all" style={{ height: `${(count / maxMes) * 80}px`, minHeight: '4px', backgroundColor: BRAND }} />
                            <span className="text-xs text-gray-500 whitespace-nowrap">{formatarMes(mes)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── ABA POR PESSOA ── */}
              {abaAtiva === 'pessoa' && (
                <div className="space-y-4">
                  <input value={busca} onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por nome ou CPF..."
                    className="w-full px-3.5 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none" />
                  {pessoasFiltradas.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nenhum registro encontrado.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2">Nome</th>
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2">CPF</th>
                            <th className="text-center text-xs font-semibold text-gray-500 pb-2">Solicitações</th>
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden sm:table-cell">Último Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pessoasFiltradas.map(([nome, d], i) => (
                            <tr key={nome} className={`${i > 0 ? 'border-t border-gray-50' : ''}`}>
                              <td className="py-2 text-gray-800 font-medium">{nome}</td>
                              <td className="py-2 text-gray-500 font-mono text-xs">{d.cpf}</td>
                              <td className="py-2 text-center">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: BRAND }}>{d.total}</span>
                              </td>
                              <td className="py-2 hidden sm:table-cell">
                                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_COLORS[d.ultStatus as any] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>{d.ultStatus}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── ABA POR ÁRVORE ── */}
              {abaAtiva === 'arvore' && (
                <div className="space-y-4">
                  <input value={busca} onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por tipo de árvore..."
                    className="w-full px-3.5 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none" />
                  {arvoresFiltradas.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nenhum registro encontrado.</p>
                  ) : (
                    <div className="space-y-2.5">
                      {arvoresFiltradas.map(([tipo, count]) => {
                        const pct = filtradas.length > 0 ? (count / filtradas.length) * 100 : 0
                        return (
                          <div key={tipo} className="flex items-center gap-3">
                            <div className="w-44 text-xs text-gray-700 font-medium flex-shrink-0 truncate flex items-center gap-2">
                              <TreePine className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND }} />
                              {tipo}
                            </div>
                            <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                              <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#1a5470' }} />
                            </div>
                            <span className="text-xs font-bold text-gray-800 w-8 text-right">{count}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ── ABA POR RUA ── */}
              {abaAtiva === 'rua' && (
                <div className="space-y-4">
                  <input value={busca} onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por rua ou bairro..."
                    className="w-full px-3.5 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none" />
                  {ruasFiltradas.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nenhum registro encontrado.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2">Rua</th>
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden sm:table-cell">Bairro</th>
                            <th className="text-center text-xs font-semibold text-gray-500 pb-2">Solicitações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ruasFiltradas.map(([rua, d], i) => (
                            <tr key={rua} className={`${i > 0 ? 'border-t border-gray-50' : ''}`}>
                              <td className="py-2 text-gray-800 font-medium flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: BRAND }} />
                                {rua}
                              </td>
                              <td className="py-2 text-gray-500 hidden sm:table-cell">{d.bairro || '—'}</td>
                              <td className="py-2 text-center">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: BRAND }}>{d.total}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── ABA REPLANTIO ── */}
              {abaAtiva === 'replantio' && (
                <div className="space-y-5">
                  {/* KPIs visuais */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Replantios concluídos', value: comReplantio.length, color: 'text-green-700', bg: 'bg-green-50' },
                      { label: 'Dentro do prazo (15 dias)', value: dentroPrazo.length, color: 'text-green-800', bg: 'bg-green-100' },
                      { label: 'Fora do prazo', value: foraPrazo.length, color: 'text-red-600', bg: 'bg-red-50' },
                      { label: 'Aguardando replantio', value: semReplantio.length, color: 'text-orange-600', bg: 'bg-orange-50' },
                    ].map(c => (
                      <div key={c.label} className={`${c.bg} rounded-2xl border border-gray-100 p-4`}>
                        <p className="text-xs text-gray-500 mb-1 leading-tight">{c.label}</p>
                        <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Métricas gerais */}
                  <div className="bg-gray-50 rounded-2xl p-4 flex flex-wrap gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Total de árvores removidas</p>
                      <p className="text-lg font-bold text-gray-800">{totalArvoresRem}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total de árvores replantadas</p>
                      <p className="text-lg font-bold text-green-700">{totalReplantadas}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Taxa de replantio</p>
                      <p className="text-lg font-bold text-[#0f3b50]">
                        {filtradas.length > 0 ? Math.round((comReplantio.length / filtradas.length) * 100) : 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Taxa dentro do prazo</p>
                      <p className="text-lg font-bold text-green-700">
                        {comReplantio.length > 0 ? Math.round((dentroPrazo.length / comReplantio.length) * 100) : 0}%
                      </p>
                    </div>
                  </div>

                  {/* Replantios concluídos */}
                  {comReplantio.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-600" />
                        Replantios Realizados ({comReplantio.length})
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2">Protocolo</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden sm:table-cell">Solicitante</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2">Tipo Replantada</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden md:table-cell">Local</th>
                              <th className="text-center text-xs font-semibold text-gray-500 pb-2">Prazo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {comReplantio.map((s, i) => (
                              <tr key={s._id} className={`${i > 0 ? 'border-t border-gray-50' : ''}`}>
                                <td className="py-2 font-mono text-xs font-semibold text-gray-800">{s.protocolo}</td>
                                <td className="py-2 text-xs text-gray-700 hidden sm:table-cell">{s.nomeCompleto || '—'}</td>
                                <td className="py-2 text-xs text-gray-700">{(s as any).tipoArvoreReplantada || s.arvoreReplantada || '—'}</td>
                                <td className="py-2 text-xs text-gray-500 hidden md:table-cell">{(s as any).enderecoReplantio || s.ondeReplantio || (s.localReplantio === 'mesmo' ? 'Mesmo local' : '—')}</td>
                                <td className="py-2 text-center">
                                  {s.dentroDoPrezo === true  && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium border border-green-200">Sim</span>}
                                  {s.dentroDoPrezo === false && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium border border-red-200">Não</span>}
                                  {s.dentroDoPrezo === undefined && <span className="text-xs text-gray-400">—</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Aguardando replantio */}
                  {semReplantio.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                        Aguardando Replantio ({semReplantio.length})
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2">Protocolo</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2">Solicitante</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden sm:table-cell">CPF</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden md:table-cell">Tipo Árvore</th>
                              <th className="text-left text-xs font-semibold text-gray-500 pb-2">Data</th>
                            </tr>
                          </thead>
                          <tbody>
                            {semReplantio.map((s, i) => (
                              <tr key={s._id} className={`${i > 0 ? 'border-t border-gray-50' : ''}`}>
                                <td className="py-2 font-mono text-xs font-semibold text-gray-800">{s.protocolo}</td>
                                <td className="py-2 text-xs text-gray-700">{s.nomeCompleto || '—'}</td>
                                <td className="py-2 text-xs text-gray-500 font-mono hidden sm:table-cell">{s.cpf || '—'}</td>
                                <td className="py-2 text-xs text-gray-500 hidden md:table-cell">{s.tipoArvore || '—'}</td>
                                <td className="py-2 text-xs text-gray-500">{formatarData(s.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {comReplantio.length === 0 && semReplantio.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-8">Nenhum dado de replantio no período selecionado.</p>
                  )}
                </div>
              )}

              {/* ── ABA POR CPF ── */}
              {abaAtiva === 'cpf' && (
                <div className="space-y-4">
                  <input value={busca} onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por CPF ou nome..."
                    className="w-full px-3.5 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none" />
                  {cpfsFiltrados.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">Nenhum registro encontrado.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2">CPF</th>
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2">Nome</th>
                            <th className="text-center text-xs font-semibold text-gray-500 pb-2">Solicitações</th>
                            <th className="text-left text-xs font-semibold text-gray-500 pb-2 hidden sm:table-cell">Histórico detalhado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cpfsFiltrados.map(([cpf, d], i) => {
                            const historicoDosCPF = filtradas.filter(s => s.cpf === cpf)
                            return (
                              <tr key={cpf} className={`${i > 0 ? 'border-t border-gray-50' : ''}`}>
                                <td className="py-2 font-mono text-xs text-gray-800 font-medium">{cpf}</td>
                                <td className="py-2 text-gray-700">{d.nome}</td>
                                <td className="py-2 text-center">
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: BRAND }}>{d.total}</span>
                                </td>
                                <td className="py-2 hidden sm:table-cell">
                                  <div className="flex flex-wrap gap-1">
                                    {historicoDosCPF.slice(0, 3).map(s => (
                                      <span key={s._id} className={`text-xs px-1.5 py-0.5 rounded-full border font-medium ${STATUS_COLORS[s.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                                        {formatarData(s.createdAt)} – {s.status}
                                      </span>
                                    ))}
                                    {historicoDosCPF.length > 3 && (
                                      <span className="text-xs text-gray-400">+{historicoDosCPF.length - 3} mais</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Tabela listagem — colunas limpas e alinhadas */}
          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Cabeçalho da listagem */}
            <div className="flex items-center justify-between px-5 pt-4 pb-3" style={{ borderBottom: `2px solid #0f3b50` }}>
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 rounded-full" style={{ backgroundColor: BRAND }} />
                <h2 className="text-sm font-bold" style={{ color: BRAND }}>Listagem do Período</h2>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: BRAND }}>
                {filtradas.length} reg.
              </span>
            </div>
            {filtradas.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f0f7fa' }}>
                  <BarChart2 className="w-5 h-5" style={{ color: BRAND }} />
                </div>
                <p className="text-sm text-gray-400">Nenhum registro no período selecionado.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: BRAND }}>
                      <th className="text-left text-xs font-bold px-4 py-3 text-white tracking-wide">Protocolo</th>
                      <th className="text-left text-xs font-bold px-3 py-3 text-white tracking-wide hidden sm:table-cell">Solicitante</th>
                      <th className="text-left text-xs font-bold px-3 py-3 text-white tracking-wide hidden md:table-cell">Árvore</th>
                      <th className="text-left text-xs font-bold px-3 py-3 text-white tracking-wide">Data</th>
                      <th className="text-left text-xs font-bold px-3 py-3 pr-4 text-white tracking-wide">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtradas.slice(0, 100).map((s, i) => (
                      <tr
                        key={s._id}
                        className="hover:bg-blue-50/40 transition-colors"
                        style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8fbfd', borderBottom: '1px solid #e9f0f4' }}
                      >
                        <td className="px-4 py-2.5 font-mono text-xs font-semibold text-gray-800 whitespace-nowrap">{s.protocolo}</td>
                        <td className="px-3 py-2.5 hidden sm:table-cell text-xs text-gray-700 max-w-[180px] truncate">{s.nomeCompleto || '—'}</td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-xs text-gray-500 max-w-[110px] truncate">{s.tipoArvore || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{formatarData(s.createdAt)}</td>
                        <td className="px-3 py-2.5 pr-4">
                          <span className={`inline-block text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${STATUS_COLORS[s.status] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>{s.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtradas.length > 100 && (
                  <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <p className="text-xs text-gray-400">
                      A exibir 100 de {filtradas.length} registos. Use os filtros ou baixe o PDF para ver todos.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
